import React from 'react';
import { createBrowserRouter, Link } from 'react-router-dom';
import Dashboard from '@/pages/Dashboard';
import Login from '@/pages/auth/Login';
import Registration from '@/pages/auth/Registration';
import ConfirmRegistration from '@/pages/auth/ConfirmRegistration';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import SetPassword from '@/pages/auth/SetPassword';
import Terms from '@/pages/Terms';
import InvitationSignup from '@/pages/auth/InvitationSignup';
import Submissions from '@/pages/Submissions';
import SubmissionShow from '@/pages/Submissions/Show';
import Studies from '@/pages/Studies';
import StudyShow from '@/pages/Studies/Show';
import Sites from '@/pages/Sites';
import SiteShow from '@/pages/Sites/Show';
import DesignSystem from '@/pages/DesignSystem';
import AppLayout from '@/components/AppLayout';
import DesignSystemLayout from '@/components/DesignSystemLayout';
import { TermsGateWrapper } from '@/components/TermsGate';
import SubmissionForm from './pages/SubmissionForm';
import PreFormWizard from './features/submission_forms/PreFormWizard';
import StudiesNew from '@/pages/Studies/New';
import SitesNew from '@/pages/Sites/New';
import NewInvitationPage from './pages/admin_workflows/invitations/New';
import { MultiFactorAuthGateWrapper } from './components/MultiFactorAuthGate';
import SubmissionTemplateWorkspace from './pages/admin_workflows/submission_templates/Workspace';
import SubmissionTemplateEditRedirect from './pages/admin_workflows/submission_templates/EditRedirect';
import SubmissionTemplatesIndex from './pages/admin_workflows/submission_templates/Index';
import QuestionsIndex from './pages/admin_workflows/questions/Index';
import NewBannerMessagePage from './pages/admin_workflows/banner_messages/New';
import NewRoleAssignmentPage from './pages/admin_workflows/role_assignments/New';
import EditRoleAssignmentPage from './pages/admin_workflows/role_assignments/Edit';
import DocxViewer from './pages/DocxViewer';
import NotFound from '@/pages/NotFound';
import ZipExportPage from '@/pages/Downloads/ZipExportPage';
import NotificationPreferences from '@/pages/Settings/NotificationPreferences';

// Protected Route wrapper - will be passed from App.tsx
export const createRouterWithAuth = (
  ProtectedRoute: React.ComponentType<{ children: React.ReactNode }>
) => {
  return createBrowserRouter([
    // Public routes
    {
      path: '/',
      element: <Login />,
    },
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/register',
      element: <Registration />,
    },
    {
      path: '/confirm-registration',
      element: <ConfirmRegistration />,
    },
    {
      path: '/forgot-password',
      element: <ForgotPassword />,
    },
    {
      path: '/set-password',
      element: <SetPassword />,
    },
    {
      path: '/invitations/accept',
      element: <InvitationSignup />,
    },
    {
      path: '/terms',
      element: <Terms />,
    },

    // Protected routes with full page layout (registered before sidebar layout so
    // static `submissions/new` wins over `submissions/:submissionId` — see Study Show "Add Submission".)
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <MultiFactorAuthGateWrapper>
            <TermsGateWrapper>
              <AppLayout fullPage />
            </TermsGateWrapper>
          </MultiFactorAuthGateWrapper>
        </ProtectedRoute>
      ),
      children: [
        {
          path: 'submissions/new',
          element: <PreFormWizard />,
          handle: {
            crumb: () => <Link to="/submissions/new">New Submission</Link>,
          },
        },
        {
          path: 'studies/new',
          element: <StudiesNew />,
          handle: {
            crumb: () => <Link to="/studies/new">New Study</Link>,
          },
        },
        {
          path: 'sites/new',
          element: <SitesNew />,
          handle: {
            crumb: () => <Link to="/sites/new">New Site</Link>,
          },
        },
        {
          path: 'submission_templates/:id',
          element: <SubmissionForm />,
          handle: {
            crumb: () => (
              <Link to="/submission_templates/:id">Submission Form</Link>
            ),
          },
        },
        {
          path: 'submissions/:submissionId/form',
          element: <SubmissionForm />,
          handle: {
            crumb: () => (
              <Link to="/submissions/:submissionId/form">Submission Form</Link>
            ),
          },
        },
      ],
    },

    // Protected routes with sidebar layout
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <MultiFactorAuthGateWrapper>
            <TermsGateWrapper>
              <AppLayout />
            </TermsGateWrapper>
          </MultiFactorAuthGateWrapper>
        </ProtectedRoute>
      ),
      handle: {
        crumb: () => <Link to="/dashboard">Home</Link>,
      },
      children: [
        {
          path: 'dashboard',
          element: <Dashboard />,
          handle: {
            crumb: () => 'Dashboard',
          },
        },
        {
          path: 'submissions',
          element: <Submissions />,
          handle: {
            crumb: () => <Link to="/submissions">Submissions</Link>,
          },
        },
        {
          path: 'submissions/:submissionId',
          element: <SubmissionShow />,
        },
        {
          path: 'studies',
          element: <Studies />,
          handle: {
            crumb: () => <Link to="/studies">Studies</Link>,
          },
        },
        {
          path: 'studies/:studyId',
          element: <StudyShow />,
        },
        {
          path: 'sites',
          element: <Sites />,
          handle: {
            crumb: () => <Link to="/sites">Sites</Link>,
          },
        },
        {
          path: 'sites/:siteId',
          element: <SiteShow />,
        },
        {
          // OV-1405: the link in the "your documents are ready" e-mail.
          path: 'downloads/:exportId',
          element: <ZipExportPage />,
          handle: {
            crumb: () => 'Download',
          },
        },
        {
          // OV-1293: the page itself redirects to /dashboard when
          // notificationPersonalizationEnabled is false (see
          // NotificationPreferences.tsx) — the router config can't
          // conditionally omit a route at this point since user data isn't
          // fetched yet when createRouterWithAuth runs, so "fully dormant"
          // is enforced at render time instead of at route-table time.
          path: 'settings/notifications',
          element: <NotificationPreferences />,
          handle: {
            crumb: () => 'Notification preferences',
          },
        },
      ],
    },

    // Design System route with special layout
    {
      path: '/design-system',
      element: (
        <ProtectedRoute>
          <DesignSystemLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <DesignSystem />,
        },
      ],
    },

    {
      path: '/admin_workflows/banner_messages/new',
      element: <NewBannerMessagePage />,
    },

    {
      path: '/admin_workflows/role_assignments/new',
      element: <NewRoleAssignmentPage />,
    },

    {
      path: '/admin_workflows/role_assignments/:id/edit',
      element: <EditRoleAssignmentPage />,
    },

    {
      path: '/admin_workflows/invitations/new',
      element: <NewInvitationPage />,
    },

    {
      path: '/admin_workflows/submission_templates/:id',
      element: <SubmissionTemplateEditRedirect />,
    },

    {
      path: '/admin_workflows/submission_templates/:id/edit',
      element: <SubmissionTemplateWorkspace tab="edit" />,
    },

    {
      path: '/admin_workflows/submission_templates/:id/history',
      element: <SubmissionTemplateWorkspace tab="history" />,
    },

    {
      path: '/admin_workflows/submission_templates',
      element: <SubmissionTemplatesIndex />,
    },

    {
      path: '/admin_workflows/questions',
      element: <QuestionsIndex />,
    },

    // Full-page DOCX viewer (opened in new tab)
    {
      path: '/docx-viewer',
      element: (
        <ProtectedRoute>
          <DocxViewer />
        </ProtectedRoute>
      ),
    },

    // Catch-all 404. Deliberately not a redirect to /login: for a signed-in user
    // a mistyped or stale URL is not a session problem, and bouncing them to a
    // login screen misrepresents what happened.
    {
      path: '*',
      element: <NotFound />,
    },
  ]);
};
